import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { track } from '@adc/analytics'
import { Filter, Icon, Modal, constants } from '@adc/ui-adc'
import { search } from '@adc/blueprint'
import { match } from 'ts-pattern'
import { formatLocation } from '../../converters/location'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const { ColorNames } = constants

const DrawerOption = ({ isSelected, text, elmId, onClick }) => (
  <>
    <hr className={cx('drawer-divider')} />
    <div
      className={cx('drawer-option', { selected: isSelected })}
      data-elm-id={elmId}
      onClick={onClick}
    >
      <div className={cx('drawer-option-content')}>
        {isSelected && (
          <i className={cx('drawer-check-icon', 'far', 'fa-check')} />
        )}
        <span className={cx('drawer-option-text', { selected: isSelected })}>
          {text}
        </span>
      </div>
    </div>
  </>
)

DrawerOption.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  elmId: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
}

const EXPERIMENT_FLAG_KEY = 'fe.resi.srp-sort-experiment-v1'

const MobileFilterBar = ({
  data,
  actions,
  updateFiltersAction,
  isMapView,
  nearbyConfig,
  featureFlags,
}) => {
  const {
    filtersData,
    currentFilters,
    isLoading,
    listings,
    currentUiFilterGroups,
  } = data
  const resultsAmount = listings?.total_count
  const nearbyTotal = listings?.nearbyTotal || 0

  const [sortDrawerOpen, setSortDrawerOpen] = useState(false)
  const [nearbyDrawerOpen, setNearbyDrawerOpen] = useState(false)

  const { type: sortType, order: sortOrder } = currentFilters?.sort || {}
  const mappedSortValue = search.sorting.utils.mapSortValue(sortType, sortOrder)

  const selectedSortOption = search.sorting.constants.ARVSortOptionsOrder.find(
    option => option.value === mappedSortValue
  )

  const handleSortSelect = useCallback(
    value => {
      track('sort', { sortSelection: value })
      const updatedSortValue = search.sorting.utils.getUpdatedSortObject(
        value,
        currentFilters
      )
      updateFiltersAction({
        sort: {
          ...updatedSortValue,
          order: updatedSortValue.order || '',
        },
      })
      setSortDrawerOpen(false)
    },
    [currentFilters, updateFiltersAction]
  )

  const experimentFlag = featureFlags?.[EXPERIMENT_FLAG_KEY]
  const inExperiment = experimentFlag === 'treatment'

  const sortOptions = search.sorting.constants.ARVSortOptionsOrder.filter(
    sortOption =>
      inExperiment ||
      sortOption.value !== search.sorting.constants.WEB_SORT_TYPES.recommended
  )

  const { showNearbyCheckbox, nearbyOptions, defaultNearbyRadius } =
    nearbyConfig || {}
  const locationFilters = currentUiFilterGroups?.locationFilterGroup || {}
  const nearbySearchFilters =
    currentUiFilterGroups?.nearbySearchFilterGroup || {}
  const combinedFilters = { ...locationFilters, ...nearbySearchFilters }
  const { useNearbySearch, nearbySearchRadius } = combinedFilters
  const isNearbySearch = useNearbySearch !== 'n'
  const nearbySearchValue = nearbySearchRadius || defaultNearbyRadius || 50

  const searchQuery = currentFilters && currentFilters.search

  const getResultsSuffix = (type = 'in') => {
    const location = formatLocation(locationFilters)
    if (searchQuery) return ` results related to ${searchQuery}`
    if (location) return ` results ${type} ${location}`
    return ' results'
  }

  const getNoResultsText = () => {
    const location = formatLocation(locationFilters)
    if (searchQuery) return `Not matching properties for ${searchQuery}`
    if (isNearbySearch && location)
      return `No results within ${nearbySearchValue} miles of ${location}`
    if (location) return `No results in ${location}`
    return 'Not matching properties'
  }

  const inLocationCount = isNearbySearch ? nearbyTotal : resultsAmount || 0
  const nearbyCount =
    isNearbySearch && resultsAmount != null ? resultsAmount - nearbyTotal : 0

  const handleNearbySelect = useCallback(
    value => {
      track('search-area-filter', {
        filterValue: value === 'location-only' ? 'Exact' : String(value),
      })
      if (value === 'location-only') {
        updateFiltersAction({ useNearbySearch: 'n' })
      } else {
        updateFiltersAction({
          useNearbySearch: 'y',
          nearbySearchRadius: value,
        })
      }
      setNearbyDrawerOpen(false)
    },
    [updateFiltersAction]
  )

  const allNearbyOptions = useMemo(() => {
    const opts = (nearbyOptions || []).map(o => ({ ...o }))
    opts.push({ value: 'location-only', text: 'Within Location Only' })
    return opts
  }, [nearbyOptions])

  const nearbySelectedValue = isNearbySearch
    ? nearbySearchValue
    : 'location-only'

  const moreFiltersDefinition = useMemo(() => {
    return filtersData?.filterDefinitions?.find(d => d.name === 'moreFilters')
  }, [filtersData?.filterDefinitions])

  const moreFiltersData = filtersData?.filterViewModels?.moreFilters

  if (isMapView) {
    return null
  }

  return (
    <>
      <div className={cx('mobile-filter-bar-spacer')} />

      <div
        className={cx('mobile-results-summary')}
        data-elm-id='mobile_results_summary'
      >
        {!isLoading && resultsAmount != null && (
          <span className={cx('results-summary-text')}>
            {match({
              hasExact: inLocationCount > 0,
              hasNearby: nearbyCount > 0,
            })
              .with({ hasExact: false, hasNearby: false }, () => (
                <span data-elm-id='mobile_results_summary_no_results'>
                  {getNoResultsText()}
                </span>
              ))
              .with({ hasExact: true, hasNearby: true }, () => {
                const location = formatLocation(locationFilters)
                return (
                  <>
                    <span data-elm-id='mobile_results_summary_count'>
                      {inLocationCount.toLocaleString('en')}
                    </span>
                    {location
                      ? ` results in ${location} and `
                      : ' results and '}
                    <span data-elm-id='mobile_results_summary_nearby_count'>
                      {nearbyCount.toLocaleString('en')}
                    </span>
                    {' nearby'}
                  </>
                )
              })
              .with({ hasExact: true, hasNearby: false }, () => (
                <>
                  <span data-elm-id='mobile_results_summary_count'>
                    {inLocationCount.toLocaleString('en')}
                  </span>
                  {getResultsSuffix('in')}
                </>
              ))
              .with({ hasExact: false, hasNearby: true }, () => (
                <>
                  <span data-elm-id='mobile_results_summary_nearby_count'>
                    {nearbyCount.toLocaleString('en')}
                  </span>
                  {getResultsSuffix('nearby')}
                </>
              ))
              .exhaustive()}
          </span>
        )}
      </div>

      <div className={cx('mobile-filter-bar')} data-elm-id='mobile_filter_bar'>
        {moreFiltersDefinition && moreFiltersData && (
          <div onClick={() => track('srp-filters-button-click')}>
            <Filter
              data={moreFiltersData}
              definition={moreFiltersDefinition}
              elmId='mobile_moreFilters_filter'
              isLoading={isLoading}
              resultsAmount={resultsAmount}
              onChange={(key, value) =>
                actions?.onFancyFilterChange(moreFiltersDefinition, key, value)
              }
              onReset={() => actions?.onFancyFilterReset(moreFiltersDefinition)}
              useEnhancedDrawer
              variant='pill'
            />
          </div>
        )}

        <button
          type='button'
          className={cx('dropdown-button')}
          data-elm-id='mobile_sort_dropdown_button'
          onClick={() => setSortDrawerOpen(true)}
        >
          <span className={cx('dropdown-label')}>
            Sort: {selectedSortOption?.text || 'Default'}
          </span>
          <Icon
            name='chevron-down'
            iconStyle='solid'
            color={ColorNames.Gray}
            size='small'
          />
        </button>

        {showNearbyCheckbox && (
          <button
            type='button'
            className={cx('dropdown-button')}
            data-elm-id='mobile_nearby_dropdown_button'
            onClick={() => setNearbyDrawerOpen(true)}
          >
            <span className={cx('dropdown-label')}>
              {isNearbySearch ? `${nearbySearchValue} mi` : 'Location Only'}
            </span>
            <Icon
              name='chevron-down'
              iconStyle='solid'
              color={ColorNames.Gray}
              size='small'
            />
          </button>
        )}
      </div>

      <Modal
        open={sortDrawerOpen}
        onClose={() => setSortDrawerOpen(false)}
        slideUp='always'
        fullScreen='never'
        title='Sort by:'
        elmId='mobile_sort_drawer'
      >
        <div
          className={cx('drawer-options')}
          data-elm-id='mobile_sort_dropdown'
        >
          {sortOptions.map(sortOption => (
            <DrawerOption
              key={sortOption.value}
              isSelected={sortOption.value === mappedSortValue}
              text={sortOption.text}
              elmId={`mobile_sort_dropdown_${sortOption.value}`}
              onClick={() => handleSortSelect(sortOption.value)}
            />
          ))}
        </div>
      </Modal>

      {showNearbyCheckbox && (
        <Modal
          open={nearbyDrawerOpen}
          onClose={() => setNearbyDrawerOpen(false)}
          slideUp='always'
          fullScreen='never'
          title='Search Area:'
          elmId='mobile_nearby_drawer'
        >
          <div
            className={cx('drawer-options')}
            data-elm-id='mobile_nearby_dropdown'
          >
            {allNearbyOptions.map(option => (
              <DrawerOption
                key={option.value}
                isSelected={
                  String(nearbySelectedValue) === String(option.value)
                }
                text={option.text}
                elmId={`mobile_nearby_dropdown_${option.value}`}
                onClick={() => handleNearbySelect(option.value)}
              />
            ))}
          </div>
        </Modal>
      )}
    </>
  )
}

MobileFilterBar.propTypes = {
  data: PropTypes.shape({
    filtersData: PropTypes.shape({
      filterDefinitions: PropTypes.array,
      filterViewModels: PropTypes.object,
    }),
    currentUiFilterGroups: PropTypes.object,
    currentFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    listings: PropTypes.shape({
      total_count: PropTypes.number,
      nearbyTotal: PropTypes.number,
    }),
  }).isRequired,
  actions: PropTypes.shape({
    onFancyFilterChange: PropTypes.func,
    onFancyFilterReset: PropTypes.func,
  }),
  updateFiltersAction: PropTypes.func.isRequired,
  isMapView: PropTypes.bool,
  nearbyConfig: PropTypes.shape({
    showNearbyCheckbox: PropTypes.bool,
    nearbyOptions: PropTypes.array,
    defaultNearbyRadius: PropTypes.number,
  }),
  featureFlags: PropTypes.object,
}

export default MobileFilterBar
